import {
  signIn,
  signOut,
  signUp,
  confirmSignUp,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  type SignInInput,
  type SignUpInput,
} from 'aws-amplify/auth';
import { jwtDecode } from 'jwt-decode';

export interface UserCredentials {
  email: string;
  password: string;
}

export interface SignUpData extends UserCredentials {
  givenName: string;
  familyName: string;
  nickname?: string;
  phoneNumber?: string;
}

export interface AuthenticatedUser {
  username: string;
  userId: string;
  email?: string;
  givenName?: string;
  familyName?: string;
  attributes?: Record<string, string>;
}

// Sign in with email and password
export async function handleSignIn(credentials: UserCredentials) {
  const input: SignInInput = {
    username: credentials.email,
    password: credentials.password,
  };
  return signIn(input);
}

// Sign out
export async function handleSignOut(): Promise<void> {
  await signOut();
  localStorage.clear();
  sessionStorage.clear();
}

// Sign up new user
export async function handleSignUp(data: SignUpData) {
  const phoneNumber = data.phoneNumber
    ? data.phoneNumber.startsWith('+') ? data.phoneNumber : `+${data.phoneNumber}`
    : undefined;

  const input: SignUpInput = {
    username: data.email,
    password: data.password,
    options: {
      userAttributes: {
        email: data.email,
        given_name: data.givenName,
        family_name: data.familyName,
        ...(data.nickname && { nickname: data.nickname }),
        ...(phoneNumber && { phone_number: phoneNumber }),
      },
    },
  };
  return signUp(input);
}

// Confirm sign up with code
export async function handleConfirmSignUp(email: string, code: string) {
  return confirmSignUp({
    username: email,
    confirmationCode: code,
  });
}

// Request password reset
export async function handleResetPassword(email: string) {
  return resetPassword({ username: email });
}

// Confirm password reset with code
export async function handleConfirmResetPassword(
  email: string,
  code: string,
  newPassword: string
) {
  return confirmResetPassword({
    username: email,
    confirmationCode: code,
    newPassword,
  });
}

// Check if user is logged in
export async function isLoggedIn(): Promise<boolean> {
  try {
    const currentUser = await getCurrentUser();
    const session = await fetchAuthSession();
    return !!currentUser && !!session;
  } catch {
    return false;
  }
}

// Get current user
export async function getAuthenticatedUser(): Promise<AuthenticatedUser | null> {
  try {
    const currentUser = await getCurrentUser();
    const attributes = await fetchUserAttributes();

    return {
      username: currentUser.username,
      userId: currentUser.userId,
      email: attributes.email,
      givenName: attributes.given_name,
      familyName: attributes.family_name,
      attributes: attributes as Record<string, string>,
    };
  } catch {
    return null;
  }
}

// Get JWT token for API calls
export async function getJWTToken(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch (error) {
    console.error('Error fetching auth session:', error);
    return undefined;
  }
}

// Check if token is expired
export function isTokenExpired(token: string): boolean {
  try {
    const decoded = jwtDecode<{ exp: number }>(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  } catch {
    return true;
  }
}

// Get user attributes
export async function getUserAttributes() {
  return fetchUserAttributes();
}
